import React, {
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  IconButton,
  Typography,
  CircularProgress,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { compose } from 'recompose';
import { observer } from 'mobx-react';

import UserContext from '../../../../context/UserContext';
import ActionItemsContext from '../../../context/ActionItemsContext';
import LeadModal from '../../../components/Leads/components/v2/LeadModal/LeadModal';
import Lead from '../../../Model/Lead';
import useToast from '../../../hooks/useToast';
import useComponentMounted from '../../../../hooks/useComponentMounted';
import useToolsNavigation from '../../../hooks/useToolsNavigation';
import { leadModalTabs } from '../../../components/Leads/components/v2/LeadModal/utils';
import ActionItemCard from './ActionItemCard';
import {
  StyledDrawer,
  DrawerHeader,
  DrawerBody,
  ItemList,
} from './styles';
import texts from './texts.json';

const ActionItemsDrawer = ({ open, onClose }) => {
  const { userDoc } = useContext(UserContext);
  const {
    actionItems,
    markAsRead,
    resolveItem,
    isReady,
  } = useContext(ActionItemsContext);

  const [selectedLeadDoc, setSelectedLeadDoc] = useState(null);
  const isFetchingRef = useRef(false);
  const [dismissingIds, setDismissingIds] = useState(new Set());

  const { showToast } = useToast();
  const isComponentMountedRef = useComponentMounted();
  const { navigateTo } = useToolsNavigation();

  const handleAction = useCallback(async (item) => {
    if (isFetchingRef.current) return;

    markAsRead(item.id);

    if (userDoc?.isSDR) {
      onClose();
      navigateTo('sdrLeadInbox', { state: { initialLeadId: item.leadId } });
      return;
    }

    // For Non-SDR users close drawer then fetch lead doc and open modal
    isFetchingRef.current = true;
    onClose();

    try {
      const leadDoc = await Lead.getLeadById(item.leadId);
      if (!isComponentMountedRef.current) return;

      if (leadDoc) {
        setSelectedLeadDoc(leadDoc);
      } else {
        showToast(texts.leadNotFound, { error: true });
      }
    } catch {
      if (isComponentMountedRef.current) {
        showToast(texts.errorOpeningLead, { error: true });
      }
    } finally {
      isFetchingRef.current = false;
    }
  }, [
    markAsRead,
    userDoc,
    onClose,
    navigateTo,
    isComponentMountedRef,
    showToast,
  ]);

  const handleDismiss = useCallback(async (itemId) => {
    setDismissingIds((prev) => new Set([...prev, itemId]));
    try {
      await resolveItem(itemId);
    } catch {
      if (isComponentMountedRef.current) {
        showToast(texts.errorDismissing, { error: true });
      }
    } finally {
      if (isComponentMountedRef.current) {
        setDismissingIds((prev) => {
          const next = new Set(prev);
          next.delete(itemId);
          return next;
        });
      }
    }
  }, [resolveItem, isComponentMountedRef, showToast]);

  const isEmpty = isReady && actionItems.length === 0;
  const isCentered = !isReady || isEmpty;

  const renderBody = () => {
    if (!isReady) {
      return <CircularProgress size={24} />;
    }
    if (isEmpty) {
      return (
        <Typography variant="body2" color="text.secondary">
          {texts.noNotifications}
        </Typography>
      );
    }
    return (
      <ItemList>
        {actionItems.map((item) => (
          <ActionItemCard
            key={item.id}
            item={item}
            onAction={() => handleAction(item)}
            onDismiss={() => handleDismiss(item.id)}
            isDismissing={dismissingIds.has(item.id)}
          />
        ))}
      </ItemList>
    );
  };

  return (
    <>
      <StyledDrawer anchor="right" open={open} onClose={onClose}>
        <DrawerHeader>
          <Typography variant="h6">{texts.title}</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DrawerHeader>
        <DrawerBody $centered={isCentered}>
          {renderBody()}
        </DrawerBody>
      </StyledDrawer>
      {selectedLeadDoc && (
        <LeadModal
          lead={selectedLeadDoc}
          showModal
          onClose={() => setSelectedLeadDoc(null)}
          userId={userDoc?.id}
          userName={userDoc?.name}
          isInsideSalesView
          selectedTab={leadModalTabs.CHAT}
        />
      )}
    </>
  );
};

ActionItemsDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default compose(
  observer,
)(ActionItemsDrawer);
